import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ShoppingBag, Plus, ShieldAlert } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, resolveImageUri, formatINR } from "@/lib/api";
import { getPriceInfo } from "@/lib/pricing";
import { concernVisual } from "@/lib/concerns";
import { concernImage } from "@/lib/concernImages";
import DeliveryEstimate from "@/components/DeliveryEstimate";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";

export default function ConcernDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, verified } = useStore();

  const { data: concern } = useQuery({ queryKey: ["disease", id], queryFn: () => api.getDisease(id) });
  const { data: products = [], isLoading } = useQuery({ queryKey: ["disease-products", id], queryFn: () => api.diseaseProducts(id) });

  const dosageFor = (p) => (concern?.dosages?.[p.id]) || p.dosage || "As directed";
  const inStock = products.filter((p) => p.stock > 0);
  const packageTotal = inStock.reduce((s, p) => s + getPriceInfo(p, verified).unit, 0);

  const addAll = () => {
    if (inStock.length === 0) return;
    inStock.forEach((p) => addItem(p, 1));
    toast.success(`Added ${inStock.length} products to cart`);
  };

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-8 pb-28">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted2 hover:text-brand mb-5"><ChevronLeft size={16} /> Back</button>
        {(() => {
          const { Icon, color } = concernVisual(concern?.name || "");
          const img = concernImage(concern?.name || "");
          return (
            <div className="flex items-center gap-4">
              {img ? (
                <img src={img} alt={concern?.name || ""} className="w-20 h-20 sm:w-24 sm:h-24 rounded-[24px] object-cover shrink-0 shadow-[0_10px_24px_-8px_rgba(0,0,0,0.28)] ring-1 ring-black/5" />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-[24px] flex items-center justify-center shrink-0 shadow-[0_10px_24px_-8px_rgba(0,0,0,0.28),inset_0_1px_2px_rgba(255,255,255,0.7)] ring-1 ring-black/5" style={{ background: `linear-gradient(145deg, ${color}33, ${color}12)` }}>
                  <Icon size={48} strokeWidth={1.6} style={{ color }} />
                </div>
              )}
              <div>
                <h1 className="font-display text-4xl sm:text-5xl text-ink" data-testid="concern-title">{concern?.name || "…"}</h1>
                <p className="text-muted2 mt-2">Recommended herbal products &amp; dosage for this concern.</p>
              </div>
            </div>
          );
        })()}

        {isLoading ? (
          <p className="text-muted2 mt-10">Loading…</p>
        ) : products.length === 0 ? (
          <p className="text-muted2 mt-10">No products mapped to this concern yet.</p>
        ) : (
          <>
            {/* Dosage table */}
            <div className="mt-8 rounded-2xl border border-line overflow-hidden" data-testid="dosage-table">
              <div className="grid grid-cols-[1.4fr_1fr] bg-mist px-4 py-3 text-xs uppercase tracking-wide text-muted2 font-semibold">
                <span>Product</span><span>Recommended Dosage</span>
              </div>
              {products.map((p) => (
                <div key={p.id} className="grid grid-cols-[1.4fr_1fr] items-center gap-4 px-4 py-3 border-t border-line">
                  <Link to={`/product/${p.id}`} className="flex items-center gap-3 min-w-0">
                    <img src={resolveImageUri(p.image)} alt="" className="w-14 h-14 rounded-lg object-cover bg-mist shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-ink text-sm truncate hover:text-brand">{p.name}</p>
                      <p className="text-xs text-muted2">{formatINR(getPriceInfo(p, verified).unit)}{p.stock <= 0 ? " · Out of stock" : ""}</p>
                    </div>
                  </Link>
                  <span className="text-sm text-ink/80">{dosageFor(p)}</span>
                </div>
              ))}
            </div>

            {/* Product cards with add */}
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              {products.map((p) => {
                const info = getPriceInfo(p, verified);
                return (
                  <div key={p.id} className="flex items-center gap-3 rounded-xl border border-line p-3">
                    <img src={resolveImageUri(p.image)} alt="" className="w-20 h-20 rounded-lg object-cover bg-mist" />
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-lg text-ink leading-tight line-clamp-1">{p.name}</p>
                      <p className="text-sm font-semibold text-ink">{formatINR(info.unit)}</p>
                    </div>
                    <button disabled={p.stock <= 0} onClick={() => { addItem(p, 1); toast.success(`${p.name} added`); }} className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center disabled:opacity-40" data-testid={`concern-add-${p.id}`}><Plus size={18} /></button>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 max-w-md" data-testid="concern-delivery">
              <DeliveryEstimate />
            </div>

            <div className="mt-6 rounded-xl border border-warn/40 bg-warn/5 p-4 flex gap-3" data-testid="concern-disclaimer">
              <ShieldAlert className="text-warn shrink-0 mt-0.5" size={18} />
              <p className="text-xs text-muted2 leading-relaxed">
                <span className="font-semibold text-ink">Disclaimer:</span> These products are herbal / nutritional supplements and are <span className="font-semibold">not intended to diagnose, treat, cure, or prevent any disease</span>. The suggestions for "{concern?.name}" are for general wellness support only and are not a substitute for professional medical advice. Please consult a qualified doctor or healthcare practitioner before use, especially if you are pregnant, nursing, on medication, or have a medical condition. Results may vary from person to person.
              </p>
            </div>
          </>
        )}
      </main>

      {/* Sticky package bar */}
      {inStock.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-paper border-t border-line" data-testid="package-bar">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs text-muted2">Complete package · {inStock.length} products</p>
              <p className="font-semibold text-ink">{formatINR(packageTotal)}</p>
            </div>
            <button onClick={addAll} className="h-12 px-6 rounded-full bg-brand text-white font-semibold flex items-center gap-2 whitespace-nowrap" data-testid="add-all-to-cart"><ShoppingBag size={18} /> Add All to Cart</button>
          </div>
        </div>
      )}
      <div className="h-24" />
      <Footer />
    </div>
  );
}
